/**
 * StudioCanvas — Éditeur créatif type Canva + CapCut
 * Fabric.js v5 — Mobile-first (iPhone)
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { fabric } from 'fabric';
import {
  Type, Image as ImageIcon, Download, Trash2,
  RotateCcw, Layers, Bold, Plus, Minus, X,
  SlidersHorizontal, Save, Star
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;
const getToken = () => localStorage.getItem('admin_token');
const authHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` });

// ────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ────────────────────────────────────────────────────────────────────────────
const CANVAS_W = 1080;
const CANVAS_H = 1080;

const FONTS = ['Montserrat', 'Oswald', 'Poppins', 'Playfair Display', 'Raleway', 'Anton', 'Bebas Neue', 'Roboto'];

const BRAND_COLORS = ['#FFFFFF', '#000000', '#E63946', '#1D3557', '#2A9D8F', '#F4A261', '#6C63FF', '#FFD60A'];

const STICKERS = [
  { id: 'expert', label: '★ Expert Certifié', bg: '#1D3557', color: '#fff' },
  { id: 'garanti', label: '✓ Garantie 6 Mois', bg: '#2A9D8F', color: '#fff' },
  { id: 'rapide', label: '⚡ Réparation Express', bg: '#E63946', color: '#fff' },
  { id: 'premium', label: '◆ Pièces Premium', bg: '#6C63FF', color: '#fff' },
  { id: 'prix', label: '€ Meilleur Prix', bg: '#F4A261', color: '#fff' },
  { id: 'brand', label: 'PHONELAB 69', bg: '#000', color: '#FFD60A' },
];

// ────────────────────────────────────────────────────────────────────────────
// TEMPLATES
// ────────────────────────────────────────────────────────────────────────────
const TEMPLATES = [
  {
    id: 'split_h', name: 'Avant/Après Split', icon: '◀▶', bg: '#0a0a0f',
    desc: 'Comparaison côte à côte classique',
    apply: async (c, before, after, info) => {
      c.clear();
      c.setBackgroundColor('#0a0a0f', c.renderAll.bind(c));
      // Left: before
      if (before) await addImageToCanvas(c, before, 0, 0, CANVAS_W / 2, CANVAS_H, 'left');
      // Right: after
      if (after) await addImageToCanvas(c, after, CANVAS_W / 2, 0, CANVAS_W / 2, CANVAS_H, 'right');
      // Divider
      c.add(new fabric.Rect({ left: CANVAS_W/2 - 3, top: 0, width: 6, height: CANVAS_H, fill: '#E63946', selectable: false, evented: false }));
      // Labels
      addLabel(c, 'AVANT', CANVAS_W / 4, CANVAS_H - 80, '#fff', '#E63946', 44);
      addLabel(c, 'APRÈS', (CANVAS_W / 4) * 3, CANVAS_H - 80, '#fff', '#2A9D8F', 44);
      addBrandBadge(c, info);
      c.renderAll();
    }
  },
  {
    id: 'split_v', name: 'Avant/Après Vertical', icon: '▲▼', bg: '#111',
    desc: 'Haut/bas — idéal Stories',
    apply: async (c, before, after, info) => {
      c.clear();
      c.setBackgroundColor('#111', c.renderAll.bind(c));
      if (before) await addImageToCanvas(c, before, 0, 0, CANVAS_W, CANVAS_H / 2, 'top');
      if (after) await addImageToCanvas(c, after, 0, CANVAS_H / 2, CANVAS_W, CANVAS_H / 2, 'bottom');
      c.add(new fabric.Rect({ left: 0, top: CANVAS_H/2 - 3, width: CANVAS_W, height: 6, fill: '#E63946', selectable: false, evented: false }));
      addLabel(c, 'AVANT', CANVAS_W / 2, 40, '#fff', '#E63946', 40);
      addLabel(c, 'APRÈS', CANVAS_W / 2, CANVAS_H / 2 + 40, '#fff', '#2A9D8F', 40);
      addBrandBadge(c, info);
      c.renderAll();
    }
  },
  {
    id: 'promo_card', name: 'Carte Promo', icon: '🏷', bg: '#1D3557',
    desc: 'Photo réparée + prix + badge',
    apply: async (c, before, after, info) => {
      c.clear();
      c.setBackgroundColor('#1D3557', c.renderAll.bind(c));
      // Main after image (centered, slightly elevated)
      if (after || before) await addImageToCanvas(c, after || before, 80, 120, CANVAS_W - 160, CANVAS_H - 380, 'center');
      // Top banner
      c.add(new fabric.Rect({ left: 0, top: 0, width: CANVAS_W, height: 110, fill: '#E63946', selectable: false, evented: false }));
      addText(c, 'RÉPARATION RÉUSSIE', CANVAS_W/2, 55, '#fff', 52, true, 'Anton');
      // Bottom area
      c.add(new fabric.Rect({ left: 0, top: CANVAS_H - 260, width: CANVAS_W, height: 260, fill: '#0a1628', selectable: false, evented: false }));
      addText(c, info.brand ? `${info.brand} ${info.model || ''}` : 'Votre Téléphone', CANVAS_W/2, CANVAS_H - 210, '#FFD60A', 48, true, 'Poppins');
      addText(c, info.repair || 'Réparation Express', CANVAS_W/2, CANVAS_H - 155, '#aaa', 36, false, 'Poppins');
      addText(c, 'PHONELAB 69 — Villeurbanne', CANVAS_W/2, CANVAS_H - 50, '#fff', 28, false, 'Montserrat');
      c.renderAll();
    }
  },
  {
    id: 'quality_badge', name: 'Badge Qualité', icon: '★', bg: '#0f0f23',
    desc: 'Étoiles + certification qualité',
    apply: async (c, before, after, info) => {
      c.clear();
      c.setBackgroundColor('#0f0f23', c.renderAll.bind(c));
      if (after || before) await addImageToCanvas(c, after || before, 60, 60, CANVAS_W - 120, 680, 'center');
      // Stars
      addText(c, '★ ★ ★ ★ ★', CANVAS_W/2, 790, '#FFD60A', 64, true, 'Arial');
      addText(c, 'QUALITÉ CERTIFIÉE', CANVAS_W/2, 870, '#fff', 42, true, 'Oswald');
      addText(c, 'Pièces d\'origine · Garantie 6 mois', CANVAS_W/2, 940, '#aaa', 30, false, 'Poppins');
      addBrandBadge(c, info);
      c.renderAll();
    }
  },
  {
    id: 'dark_cinema', name: 'Cinéma Noir', icon: '🎬', bg: '#000',
    desc: 'Style professionnel sombre',
    apply: async (c, before, after, info) => {
      c.clear();
      c.setBackgroundColor('#000', c.renderAll.bind(c));
      // Black letterbox bars
      c.add(new fabric.Rect({ left: 0, top: 0, width: CANVAS_W, height: 80, fill: '#000', selectable: false, evented: false }));
      c.add(new fabric.Rect({ left: 0, top: CANVAS_H-80, width: CANVAS_W, height: 80, fill: '#000', selectable: false, evented: false }));
      if (after || before) await addImageToCanvas(c, after || before, 0, 80, CANVAS_W, CANVAS_H - 160, 'center');
      addText(c, 'THE ART OF REPAIR', CANVAS_W/2, 40, '#fff', 34, true, 'Montserrat');
      addText(c, 'PHONELAB 69', CANVAS_W/2, CANVAS_H - 40, '#FFD60A', 38, true, 'Anton');
      c.renderAll();
    }
  },
  {
    id: 'before_focus', name: 'Avant en Focus', icon: '🔍', bg: '#1a1a2e',
    desc: 'Zoom sur les dégâts avec flèche',
    apply: async (c, before, after, info) => {
      c.clear();
      c.setBackgroundColor('#1a1a2e', c.renderAll.bind(c));
      // Main before image
      if (before) await addImageToCanvas(c, before, 0, 0, CANVAS_W, CANVAS_H, 'center');
      // Overlay gradient
      const grad = new fabric.Gradient({ type: 'linear', gradientUnits: 'pixels', coords: { x1: 0, y1: 0, x2: 0, y2: CANVAS_H }, colorStops: [{ offset: 0, color: 'rgba(0,0,0,0)' }, { offset: 0.7, color: 'rgba(0,0,0,0.8)' }] });
      c.add(new fabric.Rect({ left: 0, top: 0, width: CANVAS_W, height: CANVAS_H, fill: grad, selectable: false, evented: false }));
      addText(c, 'AVANT RÉPARATION', CANVAS_W/2, CANVAS_H - 180, '#E63946', 52, true, 'Oswald');
      addText(c, info.repair || 'Écran endommagé', CANVAS_W/2, CANVAS_H - 110, '#fff', 36, false, 'Poppins');
      addBrandBadge(c, info);
      c.renderAll();
    }
  },
  {
    id: 'after_focus', name: 'Après en Focus', icon: '✨', bg: '#0d1f2d',
    desc: 'Mise en valeur du résultat final',
    apply: async (c, before, after, info) => {
      c.clear();
      c.setBackgroundColor('#0d1f2d', c.renderAll.bind(c));
      if (after || before) await addImageToCanvas(c, after || before, 0, 0, CANVAS_W, CANVAS_H, 'center');
      const grad2 = new fabric.Gradient({ type: 'linear', gradientUnits: 'pixels', coords: { x1: 0, y1: 0, x2: 0, y2: CANVAS_H }, colorStops: [{ offset: 0, color: 'rgba(0,0,0,0)' }, { offset: 0.65, color: 'rgba(0,0,0,0.85)' }] });
      c.add(new fabric.Rect({ left: 0, top: 0, width: CANVAS_W, height: CANVAS_H, fill: grad2, selectable: false, evented: false }));
      addText(c, '✓ RÉPARATION RÉUSSIE', CANVAS_W/2, CANVAS_H - 190, '#2A9D8F', 50, true, 'Oswald');
      addText(c, info.brand ? `${info.brand} ${info.model || ''}` : 'Smartphone', CANVAS_W/2, CANVAS_H - 125, '#FFD60A', 40, true, 'Poppins');
      addText(c, info.repair || 'Qualité premium · Pièces d\'origine', CANVAS_W/2, CANVAS_H - 70, '#aaa', 30, false, 'Poppins');
      addBrandBadge(c, info);
      c.renderAll();
    }
  },
  {
    id: 'story_format', name: 'Story Instagram', icon: '📱', bg: '#1a1a2e',
    desc: 'Format 9:16 — haut/bas animé',
    apply: async (c, before, after, info) => {
      c.clear();
      c.setBackgroundColor('#0a0014', c.renderAll.bind(c));
      // Top: before (45%)
      if (before) await addImageToCanvas(c, before, 40, 80, CANVAS_W - 80, 430, 'center');
      // Bottom: after (45%)
      if (after) await addImageToCanvas(c, after, 40, 580, CANVAS_W - 80, 430, 'center');
      // Middle badge
      c.add(new fabric.Rect({ left: CANVAS_W/2-120, top: 485, width: 240, height: 80, fill: '#E63946', rx: 40, ry: 40, selectable: false, evented: false }));
      addText(c, '→ RÉPARÉ ←', CANVAS_W/2, 525, '#fff', 34, true, 'Anton');
      addText(c, 'AVANT', CANVAS_W/2, 50, '#E63946', 40, true, 'Oswald');
      addText(c, 'APRÈS', CANVAS_W/2, CANVAS_H - 50, '#2A9D8F', 40, true, 'Oswald');
      addBrandBadge(c, info);
      c.renderAll();
    }
  },
];

// ────────────────────────────────────────────────────────────────────────────
// CANVAS HELPERS
// ────────────────────────────────────────────────────────────────────────────
const addImageToCanvas = (canvas, dataUrl, x, y, w, h, _position = 'center') =>
  new Promise((resolve) => {
    fabric.Image.fromURL(dataUrl, (img) => {
      const scaleX = w / img.width;
      const scaleY = h / img.height;
      const scale = Math.max(scaleX, scaleY); // cover
      img.set({
        left: x + w / 2,
        top: y + h / 2,
        originX: 'center',
        originY: 'center',
        scaleX: scale,
        scaleY: scale,
        clipPath: new fabric.Rect({ left: x, top: y, width: w, height: h, absolutePositioned: true }),
      });
      canvas.add(img);
      resolve(img);
    }, { crossOrigin: 'anonymous' });
  });

const addText = (canvas, text, x, y, color, size, bold, font = 'Montserrat') => {
  const t = new fabric.Text(text, {
    left: x, top: y, originX: 'center', originY: 'center',
    fontSize: size, fill: color, fontFamily: font,
    fontWeight: bold ? 'bold' : 'normal',
    shadow: bold ? '0px 2px 8px rgba(0,0,0,0.8)' : null,
  });
  canvas.add(t);
  return t;
};

const addLabel = (canvas, text, x, y, textColor, bgColor, size) => {
  const t = new fabric.Text(text, {
    left: x, top: y, originX: 'center', originY: 'center',
    fontSize: size, fill: textColor, fontFamily: 'Oswald',
    fontWeight: 'bold', shadow: '0 2px 12px rgba(0,0,0,0.9)',
    backgroundColor: bgColor + 'cc', padding: 12,
  });
  canvas.add(t);
  return t;
};

const addBrandBadge = (canvas, info) => {
  addText(canvas, 'PHONELAB 69 · Villeurbanne', CANVAS_W / 2, CANVAS_H - 22, 'rgba(255,255,255,0.55)', 22, false, 'Poppins');
};

// Compress image before loading
const compressDataUrl = (dataUrl) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const MAX = 1280; let { width: w, height: h } = img;
      if (w > MAX || h > MAX) { if (w > h) { h = Math.round(h * MAX / w); w = MAX; } else { w = Math.round(w * MAX / h); h = MAX; } }
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      c.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL('image/jpeg', 0.82));
    };
    img.src = dataUrl;
  });

// ────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ────────────────────────────────────────────────────────────────────────────
const SliderRow = ({ label, value, onChange, min = -1, max = 1, step = 0.05 }) => (
  <div className="flex items-center gap-2">
    <span className="text-gray-400 text-xs w-24 shrink-0">{label}</span>
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={e => onChange(parseFloat(e.target.value))}
      className="flex-1 h-1.5 bg-gray-700 rounded-full accent-purple-500 cursor-pointer" />
    <span className="text-gray-500 text-xs w-8 text-right">{value > 0 ? `+${Math.round(value * 100)}` : Math.round(value * 100)}</span>
  </div>
);

const UploadBtn = ({ label, value, onChange, id }) => {
  const ref = useRef();
  return (
    <div onClick={() => ref.current?.click()}
      className={`relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed cursor-pointer transition-all h-24 overflow-hidden
        ${value ? 'border-purple-500 bg-purple-900/10' : 'border-gray-600 bg-gray-900/50 hover:border-purple-400'}`}>
      {value
        ? <img src={value} alt="" className="w-full h-full object-cover" />
        : <><ImageIcon size={20} className="text-gray-500" /><span className="text-gray-500 text-xs mt-1">{label}</span></>}
      {value && <button onClick={e => { e.stopPropagation(); onChange(null); }}
        className="absolute top-1 right-1 bg-red-600/80 text-white rounded-full p-0.5"><X size={10} /></button>}
      <input ref={ref} id={id} type="file" accept="image/*" className="hidden"
        onChange={async e => { const f = e.target.files?.[0]; if (f) { const b64 = await new Promise(r => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.readAsDataURL(f); }); onChange(await compressDataUrl(b64)); } e.target.value = ''; }} />
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ────────────────────────────────────────────────────────────────────────────
export const StudioCanvas = () => {
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);

  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activePanel, setActivePanel] = useState('templates'); // 'templates' | 'text' | 'stickers' | 'adjust'
  const [selectedObj, setSelectedObj] = useState(null);
  const [canvasScale, setCanvasScale] = useState(0.32);
  const [info, setInfo] = useState({ brand: '', model: '', repair: '' });

  // Custom templates (sauvegardés dans MongoDB)
  const [customTemplates, setCustomTemplates] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [saveNameInput, setSaveNameInput] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);

  // Adjustments for selected image
  const [adj, setAdj] = useState({ brightness: 0, contrast: 0, saturation: 0 });

  // Text input
  const [textInput, setTextInput] = useState('');
  const [textColor, setTextColor] = useState('#FFFFFF');
  const [textFont, setTextFont] = useState('Montserrat');
  const [textSize, setTextSize] = useState(60);
  const [textBold, setTextBold] = useState(false);

  const containerRef = useRef(null);

  // ── Init canvas ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!canvasRef.current) return;
    const scale = calculateScale();
    setCanvasScale(scale);

    const fc = new fabric.Canvas(canvasRef.current, {
      width: CANVAS_W, height: CANVAS_H,
      backgroundColor: '#0a0a0f',
      preserveObjectStacking: true,
      enableRetinaScaling: false,
    });
    fabricRef.current = fc;

    // Scale canvas display
    fc.setZoom(scale);
    fc.setWidth(CANVAS_W * scale);
    fc.setHeight(CANVAS_H * scale);

    // Selection events
    fc.on('selection:created', e => handleSelection(e.selected[0]));
    fc.on('selection:updated', e => handleSelection(e.selected[0]));
    fc.on('selection:cleared', () => { setSelectedObj(null); setAdj({ brightness: 0, contrast: 0, saturation: 0 }); });
    fc.on('object:modified', saveHistory);

    // Initial state
    saveHistoryForCanvas(fc);

    return () => { try { fc.dispose(); } catch (e) { /* ignore */ } };
  }, []); // eslint-disable-line

  const calculateScale = () => {
    const w = window.innerWidth;
    const targetW = Math.min(w - 32, 600);
    return targetW / CANVAS_W;
  };

  const handleSelection = (obj) => {
    setSelectedObj(obj);
    if (obj?.type === 'image') {
      const filters = obj.filters || [];
      const bf = filters.find(f => f.type === 'Brightness');
      const cf = filters.find(f => f.type === 'Contrast');
      const sf = filters.find(f => f.type === 'Saturation');
      setAdj({
        brightness: bf ? bf.brightness : 0,
        contrast: cf ? cf.contrast : 0,
        saturation: sf ? sf.saturation : 0,
      });
      setActivePanel('adjust');
    }
  };

  // ── History ──────────────────────────────────────────────────────────────
  const saveHistory = useCallback(() => {
    const fc = fabricRef.current;
    if (!fc) return;
    saveHistoryForCanvas(fc);
  }, []);

  const saveHistoryForCanvas = (fc) => {
    const json = fc.toJSON();
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push(json);
    historyIndexRef.current = historyRef.current.length - 1;
  };

  const undo = useCallback(() => {
    const fc = fabricRef.current;
    if (!fc || historyIndexRef.current <= 0) return;
    historyIndexRef.current--;
    fc.loadFromJSON(historyRef.current[historyIndexRef.current], () => {
      fc.setZoom(canvasScale);
      fc.renderAll();
    });
  }, [canvasScale]);

  // ── Apply template ───────────────────────────────────────────────────────
  const applyTemplate = useCallback(async (template) => {
    const fc = fabricRef.current;
    if (!fc) return;
    setSelectedTemplate(template.id);
    toast.info(`Chargement du template "${template.name}"...`);
    try {
      await template.apply(fc, beforeImage, afterImage, info);
      saveHistory();
      toast.success('Template appliqué !');
    } catch (e) {
      toast.error('Erreur template: ' + e.message);
      console.error(e);
    }
  }, [beforeImage, afterImage, info, saveHistory]);

  // Auto-refresh template when images change
  useEffect(() => {
    if (selectedTemplate && (beforeImage || afterImage)) {
      const t = TEMPLATES.find(t => t.id === selectedTemplate);
      if (t) applyTemplate(t);
    }
  }, [beforeImage, afterImage]); // eslint-disable-line

  // ── Add text ─────────────────────────────────────────────────────────────
  const addTextToCanvas = useCallback(() => {
    const fc = fabricRef.current;
    if (!fc || !textInput.trim()) { toast.error('Saisissez un texte'); return; }
    const t = new fabric.IText(textInput, {
      left: CANVAS_W / 2, top: CANVAS_H / 2,
      originX: 'center', originY: 'center',
      fontSize: textSize, fill: textColor,
      fontFamily: textFont,
      fontWeight: textBold ? 'bold' : 'normal',
      shadow: '0 2px 10px rgba(0,0,0,0.7)',
    });
    fc.add(t);
    fc.setActiveObject(t);
    fc.renderAll();
    setTextInput('');
    saveHistory();
    toast.success('Texte ajouté — double-cliquez pour modifier');
  }, [textInput, textColor, textFont, textSize, textBold, saveHistory]);

  // ── Adjust selected image ────────────────────────────────────────────────
  const applyAdjustment = useCallback((adjKey, val) => {
    const fc = fabricRef.current;
    const obj = fc?.getActiveObject();
    if (!obj || obj.type !== 'image') return;

    setAdj(prev => ({ ...prev, [adjKey]: val }));

    const FilterMap = {
      brightness: [fabric.Image.filters.Brightness, 'brightness'],
      contrast: [fabric.Image.filters.Contrast, 'contrast'],
      saturation: [fabric.Image.filters.Saturation, 'saturation'],
    };
    const [FilterClass, prop] = FilterMap[adjKey];

    obj.filters = (obj.filters || []).filter(f => f.type !== FilterClass.prototype.type);
    if (val !== 0) {
      const f = new FilterClass({ [prop]: val });
      obj.filters.push(f);
    }
    obj.applyFilters();
    fc.renderAll();
  }, []);

  // ── Add sticker ──────────────────────────────────────────────────────────
  const addSticker = useCallback((sticker) => {
    const fc = fabricRef.current;
    if (!fc) return;
    const rect = new fabric.Rect({ width: 380, height: 72, fill: sticker.bg, rx: 36, ry: 36 });
    const text = new fabric.Text(sticker.label, {
      left: 190, top: 36, originX: 'center', originY: 'center',
      fontSize: 32, fill: sticker.color, fontFamily: 'Oswald', fontWeight: 'bold',
    });
    const group = new fabric.Group([rect, text], {
      left: CANVAS_W / 2 - 190,
      top: CANVAS_H / 2 - 36,
    });
    fc.add(group);
    fc.setActiveObject(group);
    fc.renderAll();
    saveHistory();
    toast.success('Badge ajouté !');
  }, [saveHistory]);

  // ── Delete selected ──────────────────────────────────────────────────────
  const deleteSelected = useCallback(() => {
    const fc = fabricRef.current;
    const obj = fc?.getActiveObject();
    if (!obj) return;
    fc.remove(obj);
    fc.discardActiveObject();
    fc.renderAll();
    setSelectedObj(null);
    saveHistory();
  }, [saveHistory]);

  // ── Bring to front ───────────────────────────────────────────────────────
  const bringToFront = useCallback(() => {
    const fc = fabricRef.current;
    fc?.getActiveObject() && fc.bringToFront(fc.getActiveObject()) && fc.renderAll();
  }, []);

  // ── Export ───────────────────────────────────────────────────────────────
  const exportCanvas = useCallback((fmt = 'png') => {
    const fc = fabricRef.current;
    if (!fc) return;
    // Temporarily reset zoom for full-res export
    const prevZoom = fc.getZoom();
    fc.setZoom(1);
    fc.setWidth(CANVAS_W);
    fc.setHeight(CANVAS_H);
    fc.renderAll();
    const dataUrl = fc.toDataURL({ format: fmt, quality: 0.95, multiplier: 1 });
    // Restore zoom
    fc.setZoom(prevZoom);
    fc.setWidth(CANVAS_W * prevZoom);
    fc.setHeight(CANVAS_H * prevZoom);
    fc.renderAll();

    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `phonelab69-studio-${Date.now()}.${fmt}`;
    a.click();
    toast.success(`Exporté en ${fmt.toUpperCase()} haute résolution !`);
  }, []);

  // ── Clear canvas ─────────────────────────────────────────────────────────
  const clearCanvas = useCallback(() => {
    if (!window.confirm('Effacer tout le canvas ?')) return;
    const fc = fabricRef.current;
    fc?.clear();
    fc?.setBackgroundColor('#0a0a0f', fc.renderAll.bind(fc));
    setSelectedTemplate(null);
    saveHistory();
  }, [saveHistory]);

  // ── Custom Templates ─────────────────────────────────────────────────────
  const fetchCustomTemplates = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/studio/templates`, { headers: authHeaders() });
      if (resp.ok) setCustomTemplates(await resp.json());
    } catch (e) { console.warn('Fetch templates:', e); }
  }, []);

  useEffect(() => { fetchCustomTemplates(); }, [fetchCustomTemplates]);

  const saveAsCustomTemplate = useCallback(async () => {
    const fc = fabricRef.current;
    if (!fc) return;
    if (!saveNameInput.trim()) { toast.error('Donnez un nom au template'); return; }
    setSavingTemplate(true);
    try {
      // Generate thumbnail (low-res preview)
      const prevZoom = fc.getZoom();
      fc.setZoom(1); fc.setWidth(CANVAS_W); fc.setHeight(CANVAS_H); fc.renderAll();
      const thumbnail = fc.toDataURL({ format: 'jpeg', quality: 0.4, multiplier: 0.15 });
      const canvasJson = fc.toJSON();
      fc.setZoom(prevZoom); fc.setWidth(CANVAS_W * prevZoom); fc.setHeight(CANVAS_H * prevZoom); fc.renderAll();

      const resp = await fetch(`${API_URL}/api/studio/templates/save`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ name: saveNameInput.trim(), canvas_json: canvasJson, thumbnail_b64: thumbnail })
      });
      if (!resp.ok) throw new Error((await resp.json()).detail);
      toast.success(`Template "${saveNameInput}" sauvegardé !`);
      setSaveNameInput('');
      setShowSaveInput(false);
      fetchCustomTemplates();
    } catch (e) {
      toast.error('Erreur: ' + e.message);
    } finally {
      setSavingTemplate(false);
    }
  }, [saveNameInput, fetchCustomTemplates]);

  const applyCustomTemplate = useCallback((template) => {
    const fc = fabricRef.current;
    if (!fc) return;
    fc.loadFromJSON(template.canvas_json, () => {
      fc.setZoom(canvasScale);
      fc.setWidth(CANVAS_W * canvasScale);
      fc.setHeight(CANVAS_H * canvasScale);
      fc.renderAll();
      saveHistory();
      toast.success(`Template "${template.name}" restauré !`);
    });
    setSelectedTemplate(`custom_${template.id}`);
  }, [canvasScale, saveHistory]);

  const deleteCustomTemplate = useCallback(async (id, name) => {
    if (!window.confirm(`Supprimer le template "${name}" ?`)) return;
    await fetch(`${API_URL}/api/studio/templates/${id}`, { method: 'DELETE', headers: authHeaders() });
    setCustomTemplates(prev => prev.filter(t => t.id !== id));
    toast.success('Template supprimé');
  }, []);

  // ────────────────────────────────────────────────────────────────────────
  // PANEL BUTTONS
  // ────────────────────────────────────────────────────────────────────────
  const PANELS = [
    { id: 'templates', icon: Layers, label: 'Templates' },
    { id: 'text', icon: Type, label: 'Texte' },
    { id: 'stickers', icon: Save, label: 'Badges' },
    { id: 'adjust', icon: SlidersHorizontal, label: 'Ajuster' },
  ];

  // ────────────────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-3 max-w-2xl mx-auto" ref={containerRef}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white">🎨 Éditeur Canvas</h2>
          <p className="text-gray-500 text-xs">Templates pro · Texte · Badges · Export HD</p>
        </div>
        <div className="flex gap-2">
          <button onClick={undo} title="Annuler" className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-400 rounded-xl transition-all active:scale-95">
            <RotateCcw size={16} />
          </button>
          <button onClick={clearCanvas} title="Effacer" className="p-2 bg-gray-800 hover:bg-gray-700 text-red-400 rounded-xl transition-all active:scale-95">
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Upload Zones */}
      <div className="grid grid-cols-2 gap-3">
        <UploadBtn label="📵 Photo AVANT" value={beforeImage} onChange={setBeforeImage} id="cv-before" />
        <UploadBtn label="✅ Photo APRÈS" value={afterImage} onChange={setAfterImage} id="cv-after" />
      </div>

      {/* Device Info (compact) */}
      <div className="grid grid-cols-3 gap-2">
        <input type="text" placeholder="Marque" value={info.brand} onChange={e => setInfo(p => ({ ...p, brand: e.target.value }))}
          className="bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-purple-400" />
        <input type="text" placeholder="Modèle" value={info.model} onChange={e => setInfo(p => ({ ...p, model: e.target.value }))}
          className="bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-purple-400" />
        <input type="text" placeholder="Réparation" value={info.repair} onChange={e => setInfo(p => ({ ...p, repair: e.target.value }))}
          className="bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-purple-400" />
      </div>

      {/* Canvas */}
      <div className="relative overflow-hidden rounded-2xl border-2 border-gray-700 bg-gray-950 shadow-2xl"
        style={{ height: `${CANVAS_H * canvasScale}px` }}>
        <canvas ref={canvasRef} />
        {!selectedTemplate && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <p className="text-gray-600 text-sm">Choisissez un template ci-dessous</p>
          </div>
        )}
      </div>

      {/* Save as custom template — inline input */}
      {showSaveInput ? (
        <div className="flex gap-2 items-center bg-yellow-900/20 border border-yellow-600/40 rounded-2xl px-3 py-2.5">
          <Star size={15} className="text-yellow-400 shrink-0" />
          <input
            type="text" value={saveNameInput} onChange={e => setSaveNameInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && saveAsCustomTemplate()}
            placeholder="Nom du template (ex: Carte Samsung)..."
            autoFocus
            className="flex-1 bg-transparent text-white text-sm focus:outline-none placeholder-gray-500"
          />
          <button onClick={saveAsCustomTemplate} disabled={savingTemplate}
            className="bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 text-black font-bold px-3 py-1.5 rounded-xl text-xs transition-all active:scale-95">
            {savingTemplate ? '...' : 'Sauver'}
          </button>
          <button onClick={() => { setShowSaveInput(false); setSaveNameInput(''); }}
            className="text-gray-500 hover:text-gray-300 transition-colors">
            <X size={16} />
          </button>
        </div>
      ) : (
        <button onClick={() => setShowSaveInput(true)} data-testid="save-template-btn"
          className="w-full flex items-center justify-center gap-2 bg-yellow-600/20 hover:bg-yellow-600/30 border border-yellow-600/40 text-yellow-400 font-semibold py-2.5 rounded-2xl text-sm transition-all active:scale-[0.98]">
          <Save size={15} /> Sauvegarder comme Mon Template
        </button>
      )}

      {/* Selected object toolbar */}
      {selectedObj && (
        <div className="flex items-center gap-2 bg-gray-800/80 rounded-2xl px-3 py-2 border border-gray-700">
          <span className="text-gray-400 text-xs flex-1">
            {selectedObj.type === 'image' ? '🖼 Image sélectionnée' : selectedObj.type === 'i-text' ? '✏️ Texte — double-clic pour modifier' : '🔷 Objet sélectionné'}
          </span>
          <button onClick={bringToFront} className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded-lg transition-all">Au premier plan</button>
          <button onClick={deleteSelected} className="p-1.5 bg-red-600/30 hover:bg-red-600/60 text-red-400 rounded-lg transition-all"><Trash2 size={14} /></button>
        </div>
      )}

      {/* Panel tabs */}
      <div className="flex bg-gray-800/60 rounded-2xl p-1 gap-1">
        {PANELS.map(p => {
          const Icon = p.icon;
          return (
            <button key={p.id} onClick={() => setActivePanel(p.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all active:scale-95
                ${activePanel === p.id ? 'bg-purple-600 text-white shadow' : 'text-gray-400 hover:text-gray-200'}`}>
              <Icon size={13} />{p.label}
            </button>
          );
        })}
      </div>

      {/* ── Panel: Templates ── */}
      {activePanel === 'templates' && (
        <div className="space-y-4">
          {/* Mes Templates (custom) */}
          {customTemplates.length > 0 && (
            <div>
              <p className="text-yellow-400 text-xs font-semibold mb-2 flex items-center gap-1.5">
                <Star size={12} /> Mes Templates sauvegardés
              </p>
              <div className="grid grid-cols-2 gap-2">
                {customTemplates.map(t => (
                  <div key={t.id}
                    className={`relative group rounded-2xl border-2 overflow-hidden cursor-pointer transition-all active:scale-[0.97]
                      ${selectedTemplate === `custom_${t.id}` ? 'border-yellow-500' : 'border-gray-700 hover:border-yellow-600/60'}`}
                    onClick={() => applyCustomTemplate(t)}>
                    {t.thumbnail_b64
                      ? <img src={t.thumbnail_b64} alt={t.name} className="w-full h-20 object-cover" />
                      : <div className="w-full h-20 bg-gray-800 flex items-center justify-center"><Star size={24} className="text-yellow-500" /></div>}
                    <div className="p-2 bg-gray-900">
                      <p className="text-white text-xs font-semibold truncate">{t.name}</p>
                      <p className="text-gray-500 text-xs">{new Date(t.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); deleteCustomTemplate(t.id, t.name); }}
                      className="absolute top-1 right-1 bg-red-600/80 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Templates prédéfinis */}
          <div>
            {customTemplates.length > 0 && <p className="text-gray-500 text-xs mb-2">Templates prédéfinis</p>}
            <div className="grid grid-cols-2 gap-3">
              {TEMPLATES.map(t => (
                <button key={t.id} onClick={() => applyTemplate(t)}
                  className={`flex flex-col items-start p-3 rounded-2xl border-2 text-left transition-all active:scale-[0.97]
                    ${selectedTemplate === t.id ? 'border-purple-500 bg-purple-900/20' : 'border-gray-700 bg-gray-800/40 hover:border-gray-600'}`}>
                  <span className="text-2xl mb-1">{t.icon}</span>
                  <span className="text-white text-sm font-semibold leading-tight">{t.name}</span>
                  <span className="text-gray-500 text-xs mt-0.5 leading-tight">{t.desc}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Panel: Text ── */}
      {activePanel === 'text' && (
        <div className="space-y-3">
          <div>
            <label className="text-gray-400 text-xs block mb-1.5">Votre texte</label>
            <input type="text" value={textInput} onChange={e => setTextInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addTextToCanvas()}
              placeholder="Saisissez votre texte..."
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-purple-400" />
          </div>
          {/* Font */}
          <div>
            <label className="text-gray-400 text-xs block mb-1.5">Police</label>
            <select value={textFont} onChange={e => setTextFont(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-400">
              {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          {/* Size + Bold */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 flex-1">
              <button onClick={() => setTextSize(s => Math.max(12, s - 4))} className="p-2 bg-gray-700 rounded-lg"><Minus size={14} className="text-gray-300" /></button>
              <span className="text-white text-sm w-8 text-center font-mono">{textSize}</span>
              <button onClick={() => setTextSize(s => Math.min(200, s + 4))} className="p-2 bg-gray-700 rounded-lg"><Plus size={14} className="text-gray-300" /></button>
            </div>
            <button onClick={() => setTextBold(b => !b)}
              className={`p-2.5 rounded-xl transition-all ${textBold ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-400'}`}>
              <Bold size={16} />
            </button>
          </div>
          {/* Colors */}
          <div>
            <label className="text-gray-400 text-xs block mb-1.5">Couleur</label>
            <div className="flex flex-wrap gap-2">
              {BRAND_COLORS.map(c => (
                <button key={c} onClick={() => setTextColor(c)}
                  className={`w-8 h-8 rounded-full border-2 transition-all active:scale-90 ${textColor === c ? 'border-white scale-110' : 'border-gray-600'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          <button onClick={addTextToCanvas}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 rounded-2xl flex items-center justify-center gap-2 transition-all active:scale-[0.98]">
            <Type size={16} /> Ajouter le texte
          </button>
        </div>
      )}

      {/* ── Panel: Stickers ── */}
      {activePanel === 'stickers' && (
        <div className="space-y-2">
          <p className="text-gray-400 text-xs mb-2">Badges PhoneLab 69 — appuyez pour ajouter</p>
          {STICKERS.map(s => (
            <button key={s.id} onClick={() => addSticker(s)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-2xl border border-gray-700 hover:border-gray-500 transition-all active:scale-[0.98]"
              style={{ backgroundColor: s.bg + '22' }}>
              <span className="font-bold text-sm" style={{ color: s.color }}>{s.label}</span>
              <Plus size={16} className="text-gray-400" />
            </button>
          ))}
          <p className="text-gray-600 text-xs pt-2">Double-cliquez sur un badge pour modifier le texte</p>
        </div>
      )}

      {/* ── Panel: Adjust ── */}
      {activePanel === 'adjust' && (
        <div className="space-y-4">
          {selectedObj?.type === 'image' ? (
            <>
              <p className="text-gray-400 text-xs">Ajustements de l'image sélectionnée</p>
              <SliderRow label="Luminosité" value={adj.brightness} onChange={v => applyAdjustment('brightness', v)} />
              <SliderRow label="Contraste" value={adj.contrast} onChange={v => applyAdjustment('contrast', v)} />
              <SliderRow label="Saturation" value={adj.saturation} onChange={v => applyAdjustment('saturation', v)} />
              <div className="flex gap-2 pt-2">
                <button onClick={() => { applyAdjustment('brightness', 0); applyAdjustment('contrast', 0); applyAdjustment('saturation', 0); }}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-xl py-2.5 text-xs font-medium transition-all">
                  Réinitialiser
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <ImageIcon size={32} className="text-gray-700 mb-2" />
              <p className="text-gray-500 text-sm">Sélectionnez une image sur le canvas</p>
              <p className="text-gray-600 text-xs mt-1">pour accéder aux ajustements</p>
            </div>
          )}
        </div>
      )}

      {/* Export */}
      <div className="grid grid-cols-2 gap-2 pt-1">
        <button onClick={() => exportCanvas('png')} data-testid="export-png-btn"
          className="flex items-center justify-center gap-2 bg-green-700 hover:bg-green-800 text-white font-bold py-3.5 rounded-2xl text-sm transition-all active:scale-95">
          <Download size={16} /> Export PNG HD
        </button>
        <button onClick={() => exportCanvas('jpeg')}
          className="flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-bold py-3.5 rounded-2xl text-sm transition-all active:scale-95">
          <Download size={16} /> Export JPG
        </button>
      </div>
    </div>
  );
};
